import type { ScreenView } from "@typedoc/contracts/capture";
import type { RecordingState, RecordingView } from "@typedoc/contracts/recordings";
import { foldPackage, type RecordingPackage } from "@typedoc/contracts/recordings/package";
import type { SessionEvent, TapeEntry } from "@typedoc/interview/events";
import { replay } from "@typedoc/interview/session";
import { regionalOptions } from "../access-client";
import { AppError } from "../app-error";
import { chatApi, type ChatApi } from "../chats/client";
import { parseChatScope } from "../chats/scope";
import { createLiveFilesPort, type FileLink, type LiveFilesPort } from "../files/live-port";
import { desktopIslandVoice, desktopShowIsland } from "./desktop";
import { createLiveRecordings, type Amendment } from "./live-port";
import {
  sampleRecordingScreens,
  sampleRecordingTape,
  sampleRecordingTrace,
} from "./sample-recording";

/**
 * A screen kept during a recording, with short-lived links to read its image and its thumbnail
 * from; `thumbnailUrl` is null for a screen without a thumbnail, or one that cannot be read.
 */
export type RecordingScreen = { screen: ScreenView; url: string; thumbnailUrl: string | null };

/**
 * A recording's package (ENG-1028) with a short-lived link to each screen's image, and to its
 * thumbnail, by screen ID. A screen whose image cannot be read has neither; one without a
 * thumbnail is left out of `thumbnails`, and is shown small from its image.
 */
export type PackageRead = {
  package: RecordingPackage;
  urls: Record<string, string>;
  thumbnails: Record<string, string>;
};

/** A package with the links of the screens it shows. */
function withLinks(pkg: RecordingPackage, shown: readonly RecordingScreen[]): PackageRead {
  return {
    package: pkg,
    urls: Object.fromEntries(shown.map(({ screen, url }) => [screen.id, url])),
    thumbnails: Object.fromEntries(
      shown.flatMap(({ screen, thumbnailUrl }) =>
        thumbnailUrl === null ? [] : [[screen.id, thumbnailUrl]],
      ),
    ),
  };
}

/**
 * How long the package view holds a list of screens before asking again (ENG-1024). Every link
 * handed out works for at least this long and a minute more, well inside Files' ten minutes.
 */
export const SCREEN_LINK_HOLD_MS = 4 * 60_000;
const LINK_SPARE_MS = SCREEN_LINK_HOLD_MS + 60_000;

/**
 * Puts links on screens and their thumbnails, signing in batches only the files whose held link
 * would not last: a list read again as new screens land does not sign the earlier ones again. A
 * file that cannot be read has no link, and its screen is left out, as one that was removed is; a
 * thumbnail that cannot be read leaves the screen without one.
 */
export function screenLinks(
  files: Pick<LiveFilesPort, "openAll">,
  now: () => number = Date.now,
): (screens: readonly ScreenView[], signal?: AbortSignal) => Promise<RecordingScreen[]> {
  const held = new Map<string, FileLink>();
  return async (screens, signal) => {
    const at = now();
    for (const [id, link] of held) if (link.expiresAt.getTime() <= at) held.delete(id);
    const lasts = (id: string) => (held.get(id)?.expiresAt.getTime() ?? 0) - at >= LINK_SPARE_MS;
    const due = screens
      .flatMap((screen) => [
        screen.fileId,
        ...(screen.thumbnailFileId ? [screen.thumbnailFileId] : []),
      ])
      .filter((id) => !lasts(id));
    if (due.length > 0) {
      for (const [id, link] of await files.openAll(due, signal)) held.set(id, link);
    }
    return screens.flatMap((screen) => {
      const link = held.get(screen.fileId);
      const thumbnail = screen.thumbnailFileId ? held.get(screen.thumbnailFileId) : undefined;
      return link ? [{ screen, url: link.url, thumbnailUrl: thumbnail?.url ?? null }] : [];
    });
  };
}

/**
 * A chat's recordings, as the chat page shows them (ENG-1015): the card in the transcript, the
 * entry in the Context rail and the package view. The island has its own port onto one recording
 * (`port.ts`); this is the chat's side of the same records, behind the build's switch: the
 * Recordings API, or a sample in this page's memory.
 */
export type ChatRecordings = {
  /** The chat's recordings, oldest first. */
  list: (threadId: string, signal?: AbortSignal) => Promise<RecordingView[]>;
  /** Starts a recording on a chat the person owns; the same `requestId` answers the same one. */
  start: (threadId: string, requestId: string, signal?: AbortSignal) => Promise<RecordingView>;
  /** A recording's whole tape, for the package view to fold. */
  tape: (recordingId: string, signal?: AbortSignal) => Promise<TapeEntry[]>;
  /**
   * The screens kept during a recording (ENG-1024), oldest first, each with a link to its image.
   * A tape's `screen.action` names its screen by `keyframe`.
   */
  screens: (recordingId: string, signal?: AbortSignal) => Promise<RecordingScreen[]>;
  /** A recording's package, laid out on one clock, with links to its screens' images. */
  package: (recordingId: string, signal?: AbortSignal) => Promise<PackageRead>;
  /** Deletes an ended recording the person made, with its tape; a going one is ended first. */
  remove: (recordingId: string, signal?: AbortSignal) => Promise<void>;
  /**
   * Changes an ended recording the person made (ENG-1029): a note, a corrected answer, or a part or
   * a stretch removed. Nothing captured is edited; the answer is the package as it now folds.
   */
  amend: (recordingId: string, change: Amendment, signal?: AbortSignal) => Promise<PackageRead>;
  /**
   * Follows a recording from its position `after`: `changed` gets the recording each time it
   * changes, with the entries that landed. The answer stops following.
   */
  watch: (
    recordingId: string,
    after: number,
    changed: (recording: RecordingView, entries: TapeEntry[]) => void,
  ) => () => void;
};

/**
 * Whether this page can show Abacor the screen. A browser cannot capture it, so only the desktop
 * app can, once its bridge can open the island (`showIsland`, bridge version 3; checked by the
 * function, as `openSignIn` is: an installed shell may be older than the page it loads). The sample
 * build can, so the flow can be tried and tested in a browser.
 */
export function canRecordHere(
  api: ChatApi = chatApi,
  desktop: unknown = globalThis.window?.desktop,
) {
  if (api === "sample") return true;
  return desktopShowIsland(desktop) !== undefined;
}

/** Whether a recording is still going: the island is open on it, and the chat may not start another. */
export const isGoing = (state: RecordingState) => state === "live" || state === "paused";

function liveChatRecordings(scope: string): ChatRecordings {
  const { userId, workspaceId } = parseChatScope(scope);
  const live = createLiveRecordings({
    userId,
    workspaceId,
    options: regionalOptions,
  });
  const linked = screenLinks(
    createLiveFilesPort({ userId, workspaceId, options: regionalOptions }),
  );
  return {
    list: (threadId, signal) => live.list(threadId, signal),
    async start(threadId, requestId, signal) {
      // Abacor's voice where the island can use it; the API starts it only where voice is set up
      // (US workspaces), and the recording asks in text otherwise.
      const { tape } = await live.create(
        { threadId, requestId, voice: desktopIslandVoice() },
        signal,
      );
      return tape.recording;
    },
    watch(recordingId, after, changed) {
      const stop = new AbortController();
      void live.watchRecording(recordingId, after, changed, stop.signal);
      return () => stop.abort();
    },
    remove: (recordingId, signal) => live.remove(recordingId, signal),
    async amend(recordingId, change, signal) {
      const read = await live.amend(recordingId, change, signal);
      return withLinks(read, await linked(read.screens, signal));
    },
    // The images' links through Files, which lets in whoever may open the recording's chat.
    async screens(recordingId, signal) {
      return linked(await live.screens(recordingId, signal), signal);
    },
    async package(recordingId, signal) {
      const read = await live.package(recordingId, signal);
      return withLinks(read, await linked(read.screens, signal));
    },
    async tape(recordingId, signal) {
      const port = live.open(recordingId);
      const entries: TapeEntry[] = [];
      for (;;) {
        const page = await port.read(entries.at(-1)?.seq ?? 0, signal);
        entries.push(...page.entries);
        if (!page.more || page.entries.length === 0) return entries;
      }
    },
  };
}

/** The sample's finished recording, on the Brightline chat (`sample-recording.ts`). */
export const SAMPLE_RECORDING_CHAT = "brightline-po-follow-up";
const SAMPLE_RECORDING_ID = "rec-brightline-1";

/** How long the sample takes to start or delete one, so a button's pending state is the real one's. */
const SAMPLE_START_MS = 300;

/** The recordings in this page's memory. `now` places the sample where the Brightline chat has it. */
export function sampleChatRecordings(
  now: Date = new Date(),
  startMs: number = SAMPLE_START_MS,
): ChatRecordings {
  // Made at 10:52 yesterday; Start came six seconds later, once the person chose what Abacor sees.
  const createdAt = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 10, 52);
  const startedAt = new Date(createdAt.getTime() + 6_000);
  const seeded = sampleRecordingTape(startedAt.getTime());
  const settled = replay(seeded);
  const recordings = new Map<string, { view: RecordingView; requestId: string | null }>([
    [
      SAMPLE_RECORDING_ID,
      {
        requestId: null,
        view: {
          id: SAMPLE_RECORDING_ID,
          threadId: SAMPLE_RECORDING_CHAT,
          intent: settled.intent,
          state: "finished",
          createdAt: createdAt.toISOString(),
          endedAt: settled.endedAt === null ? null : new Date(settled.endedAt).toISOString(),
          lastSeq: seeded.length,
        },
      },
    ],
  ]);
  const tapes = new Map<string, TapeEntry[]>([[SAMPLE_RECORDING_ID, seeded]]);
  let made = 0;

  const folded = (recordingId: string): PackageRead => {
    const found = recordings.get(recordingId);
    const tape = tapes.get(recordingId);
    if (!found || !tape) throw new AppError("NOT_FOUND");
    const sample = recordingId === SAMPLE_RECORDING_ID;
    const shown = sample ? sampleRecordingScreens(startedAt.getTime()) : [];
    const pkg = foldPackage({
      recording: found.view,
      tape: structuredClone(tape),
      screens: shown.map(({ screen }) => screen),
      trace: sample ? sampleRecordingTrace(startedAt.getTime()) : [],
      now: Date.now(),
    });
    return withLinks(pkg, shown);
  };

  return {
    async list(threadId) {
      return [...recordings.values()]
        .map(({ view }) => view)
        .filter((view) => view.threadId === threadId)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    },
    async start(threadId, requestId) {
      await new Promise((resolve) => setTimeout(resolve, startMs));
      const all = [...recordings.values()].filter(({ view }) => view.threadId === threadId);
      const again = all.find((r) => r.requestId === requestId);
      if (again) return again.view;
      if (all.some(({ view }) => isGoing(view.state))) throw new AppError("RECORDING_IN_PROGRESS");
      made += 1;
      const at = new Date();
      const view: RecordingView = {
        id: `rec-sample-${String(made)}`,
        threadId,
        intent: null,
        state: "live",
        createdAt: at.toISOString(),
        endedAt: null,
        lastSeq: 1,
      };
      recordings.set(view.id, { view, requestId });
      tapes.set(view.id, [
        {
          seq: 1,
          key: "start",
          at: at.getTime(),
          event: {
            type: "session.started",
            intent: null,
            voice: "none",
            questions: [],
          },
        },
      ]);
      return view;
    },
    // The island of a sample recording runs in its own page's memory: nothing here changes.
    watch: () => () => {},
    async tape(recordingId) {
      const tape = tapes.get(recordingId);
      if (!tape) throw new AppError("NOT_FOUND");
      return structuredClone(tape);
    },
    async screens(recordingId) {
      if (!tapes.has(recordingId)) throw new AppError("NOT_FOUND");
      // Kept a moment, so the package view's loading state is the real one's.
      await new Promise((resolve) => setTimeout(resolve, startMs));
      return recordingId === SAMPLE_RECORDING_ID ? sampleRecordingScreens(startedAt.getTime()) : [];
    },
    // Folded from the sample's records as the API folds a recording's, with the bundled pictures.
    async package(recordingId) {
      // Kept a moment, so the package view's loading state is the real one's.
      await new Promise((resolve) => setTimeout(resolve, startMs));
      return folded(recordingId);
    },
    // Added to the sample's tape as the API adds one, then folded again.
    async amend(recordingId, change) {
      const found = recordings.get(recordingId);
      const tape = tapes.get(recordingId);
      if (!found || !tape) throw new AppError("NOT_FOUND");
      await new Promise((resolve) => setTimeout(resolve, startMs));
      if (isGoing(found.view.state)) throw new AppError("RECORDING_NOT_ENDED");
      const key = `package:${change.input.key}`;
      if (!tape.some((entry) => entry.key === key)) {
        const event = sampleAmendment(change, tape);
        const entry = { seq: tape.length + 1, key, at: Date.now(), event };
        const session = replay([...tape, entry]);
        const why = session.dropped.find((d) => d.seq === entry.seq)?.why;
        if (why) {
          throw new AppError(
            why === "unknown-question"
              ? "NOT_FOUND"
              : why === "stale"
                ? "CONFLICT"
                : "VALIDATION_ERROR",
          );
        }
        tape.push(entry);
        found.view = { ...found.view, lastSeq: entry.seq };
      }
      return folded(recordingId);
    },
    async remove(recordingId) {
      await new Promise((resolve) => setTimeout(resolve, startMs));
      const found = recordings.get(recordingId);
      if (!found) throw new AppError("NOT_FOUND");
      if (isGoing(found.view.state)) throw new AppError("RECORDING_NOT_ENDED");
      recordings.delete(recordingId);
      tapes.delete(recordingId);
    },
  };
}

/**
 * The tape event a sample change makes. The sample's screens and actions put nothing on its tape,
 * so a removed one names no entry, as one that came while paused would not.
 */
function sampleAmendment(change: Amendment, tape: readonly TapeEntry[]): SessionEvent {
  switch (change.kind) {
    case "note":
      return {
        type: "person.note",
        moment: change.input.moment,
        text: change.input.text,
      };
    case "correct": {
      const { questionId, moment, text, replaces } = change.input;
      return { type: "person.corrected", questionId, moment, text, replaces };
    }
    case "remove": {
      const { part } = change.input;
      if (part.kind === "said") return { type: "person.removed", part };
      if (part.kind === "range") {
        return { type: "person.removed", part: { ...part, entries: [] } };
      }
      const entry =
        tape.find((e) => e.event.type === "screen.action" && e.event.keyframe === part.id)?.seq ??
        null;
      return {
        type: "person.removed",
        part: { kind: part.kind, id: part.id, entry },
      };
    }
  }
}

/** One per person and workspace, like the chat service (`chatClientFor`). */
const clients = new Map<string, ChatRecordings>();

export function chatRecordingsFor(scope: string, api: ChatApi = chatApi): ChatRecordings {
  if (scope === "")
    throw new Error("Recordings belong to a person in a workspace; there is no scope yet");
  let client = clients.get(scope);
  if (!client) {
    client = api === "sample" ? sampleChatRecordings() : liveChatRecordings(scope);
    clients.set(scope, client);
  }
  return client;
}

/** Sign-out: the next visitor of this browser sees none of the last one's recordings. */
export function forgetChatRecordings(): void {
  clients.clear();
}
